import React from "react"

import { Box } from '@mui/material';
import moment from 'moment';
import 'moment/locale/fr'

import logoBlanc from "../../../images/logoBlanc-pdf.png"
import logoNoir from "../../../images/logoNoir-pdf.png"
import { FAMILLES, trouverCompetence, libelleComplet, mesureDe, pertinenceDe } from "../../../utils/referentielSoftSkills"

moment.locale("fr");

/* « Rapport d'impact » PDF, par PROMO (groupe). Export react-to-pdf, comme le
 * Rapport recruteur de THE SCAN. Charte : Ink Black + Burnt Orange, Questrial,
 * coins carres, zero tiret cadratin.
 *
 * Trame initiale : review du 02/09 (E1).
 * Refonte du 03/09 sur la fiche RETOURS_HICHAM_SACLAY_COMPLE_V3 de Nawal :
 *
 *   R.1 — trois familles de SIX competences (c'etait 6 / 7 / 5). La repartition
 *         et les libelles viennent desormais de utils/referentielSoftSkills.js,
 *         source unique, plutot que de la famille declaree par le contenu.
 *
 *   R.3 — ⛔ AUCUNE CAUSALITE. Le rapport ne dit plus « la formation a produit »
 *         ni « grace a la formation ». Il CONSTATE une evolution AU COURS DU
 *         PARCOURS, et distingue ce qui est mesure, ce qui est observe et ce qui
 *         peut etre interprete. En l'absence de groupe controle, rien ne permet
 *         d'attribuer causalement l'evolution a la formation : ecrire le
 *         contraire exposerait Rising Up sur le fond scientifique comme sur le
 *         plan commercial. Les formulations ci-dessous sont de Nawal, mot pour
 *         mot : ne pas les « ameliorer » sans elle.
 */

const ENCRE = "#0A0A0B"
const ORANGE = "#D85A30"
const VERT = "#2F7A3D"
const GRIS = "#9A9A9A"

let pageDimensions = { width: "1250px", height: "1768px" }

/* Relecture finale 02/09 : ce composant recoit des donnees issues de la base.
 * Une moyenne absente (aucun apprenant n'a passe le module, categorie vide)
 * arrivait ici en `undefined` et le rapport affichait « NaN % » sur toutes ses
 * pages. Un PDF envoye a une ecole ne peut pas contenir NaN : toute valeur
 * douteuse devient 0, bornee entre 0 et 100. */
const nombre = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0
}

/* 04/09 — VERSION ANGLAISE DU RAPPORT.
 *
 * Le document partait chez un client anglophone integralement en francais : ce
 * fichier n'ouvrait jamais LanguageContext et toutes ses phrases etaient en
 * dur. La langue arrive desormais avec les donnees (`langue`, ajoutee a
 * `donneesRapport` dans Progression.js), et tout le texte du rapport est ici.
 *
 * Pourquoi un dictionnaire local plutot que LanguageContext : la copie du
 * rapport est un ensemble editorial signe Nawal, avec des gabarits de phrases
 * a trous. La garder d'un seul tenant la rend relisible ; eclatee en trente
 * clefs dans le fichier de langue, elle deviendrait impossible a relire.
 *
 * ⚠️ TRADUCTION A FAIRE RELIRE PAR NAWAL. Les paragraphes de methodologie
 * portent des engagements scientifiques et juridiques — referentiel depose sur
 * OSF, agrement CIR, absence de groupe controle. Le francais n'a pas ete
 * touche : en cas de doute, c'est lui qui fait foi. */
const langueDe = (l) => (l === "en" ? "en" : "fr")

const T = {
  fr : {
    titreCourt : "Rapport d'impact",
    pageXsurY : (p, t) => `Page ${p} sur ${t}`,
    synthese : "Synthèse",
    niveauConnaissance : "Niveau de connaissance de la promotion",
    softSkills : "soft skills",
    evolutionConnaissance : (a, b, g) =>
      `Au cours du parcours, le niveau de connaissance de la promotion passe de ${a} % à ${b} %, ` +
      `soit une évolution de ${g >= 0 ? "+" + g : g} points.`,
    finNonMesuree : (nom, a) =>
      `${nom} a été mesurée en début de parcours (${a} %). La mesure de fin n'a pas encore été réalisée\u00a0: ` +
      `aucune évolution ne peut être établie à ce stade.`,
    debutNonMesure : (nom, b) =>
      `${nom} a été mesurée en fin de parcours (${b} %). Aucune mesure de début n'est disponible\u00a0: ` +
      `aucune évolution ne peut être établie.`,
    nonMesuree : (nom) => `${nom} n'a pas encore été mesurée sur cette promotion.`,
    pasEncoreMesure : "pas encore mesuré",
    // B51 (25/09) : groupe sans aucune mesure. Texte factuel provisoire, a
    // faire valider par Nawal (auteure des textes du rapport).
    syntheseAucuneMesure : "Aucun étudiant de ce groupe n'a encore été mesuré\u00a0: les résultats apparaîtront dès les premiers modules terminés.",
    perimetre : "Ce rapport porte sur les étudiants disposant d'une mesure en début et en fin de parcours. " +
      "Les autres inscrits n'y figurent pas, faute de point de comparaison.",
    chapeauFamille : "Évolution constatée au cours du parcours, compétence par compétence\u00a0: niveau moyen en début " +
      "et en fin de parcours, ce que mesure la compétence, et son intérêt en contexte professionnel.",
    agrementCIR : "Rising Up est agréé organisme de recherche au titre du Crédit d'Impôt Recherche " +
      "par le Ministère de l'Enseignement supérieur et de la Recherche (2025 à 2027).",
    constatGlobal : "Au cours du parcours, la promotion évolue, en connaissance et sur plusieurs compétences. " +
      "Ces résultats constatent une évolution observée pendant le parcours\u00a0; en l'absence de groupe contrôle, " +
      "ils ne permettent pas, à eux seuls, de l'attribuer causalement à la formation. " +
      "La mesure distingue ce qui est mesuré, ce qui est observé et ce qui peut être interprété.",
    debutParcours : "Début de parcours",
    finParcours : "Fin de parcours",
    vueEnsemble : "Vue d'ensemble par famille",
    ceQueConstatent : "Ce que constatent ces résultats",
    methodologie : "Méthodologie et cadre de crédibilité",
    etudiantsMesures : "étudiants mesurés",
    // Chasse aux bugs du 25/09 (B51) : ces libelles etaient ecrits en dur, en
    // francais, meme dans le rapport anglais.
    diplomes : "Diplôme(s)",
    periode : "Période",
    editeLe : (d) => `Édité le ${d}`,
    moyenneGroupe : "moyenne du groupe",
    modules : "modules",
    nombreEtudiants : "Nombre d'étudiants",
    autresCompetences : "Autres compétences mesurées",
    competences : (n) => (n > 1 ? "compétences" : "compétence"),
    ceQuElleMesure : "Ce qu'elle mesure\u00a0: ",
    pertinence : "Pertinence\u00a0: ",
    pertinencePrefixe : "utile en contexte professionnel et pour l'employabilité\u00a0: ",
    seuilCourt : (v) => `seuil ${v} %`,
    plafond : " Le niveau de départ était déjà élevé\u00a0: proche du plafond, l'écart tient dans le bruit de mesure.",
    constat : (nom, verbe, a, b) => `Au cours du parcours, ${nom} ${verbe} (${a} % à ${b} %).`,
    certifAtteint : (s) => `La moyenne de la promotion dépasse le seuil de certification (${s} %).`,
    certifNonAtteint : (s) => `La moyenne de la promotion reste sous le seuil de certification (${s} %).`,
    synthesePhrase : (n, gain, hausse, stable, baisse) =>
      `Au cours du parcours, sur ${n} étudiant${n > 1 ? "s" : ""}, ` +
      `le niveau de connaissance de la promotion évolue de ${gain >= 0 ? "+" + gain : gain} points. ` +
      `Les compétences progressent sur ${hausse}, restent stables sur ${stable} ` +
      `et se réajustent sur ${baisse}.`,
  },
  en : {
    titreCourt : "Impact report",
    pageXsurY : (p, t) => `Page ${p} of ${t}`,
    synthese : "Summary",
    niveauConnaissance : "Knowledge level of the cohort",
    softSkills : "soft skills",
    evolutionConnaissance : (a, b, g) =>
      `Over the course of the programme, the cohort's knowledge level moves from ${a}% to ${b}%, ` +
      `a change of ${g >= 0 ? "+" + g : g} points.`,
    finNonMesuree : (nom, a) =>
      `${nom} was measured at the start of the programme (${a}%). The end measurement has not yet been taken: ` +
      `no change can be established at this stage.`,
    debutNonMesure : (nom, b) =>
      `${nom} was measured at the end of the programme (${b}%). No start measurement is available: ` +
      `no change can be established.`,
    nonMesuree : (nom) => `${nom} has not yet been measured for this cohort.`,
    pasEncoreMesure : "not yet measured",
    syntheseAucuneMesure : "No student in this group has been measured yet: results will appear as soon as the first modules are completed.",
    perimetre : "This report covers students with a measurement at both the start and the end of the programme. " +
      "Other enrolled students do not appear here, for want of a point of comparison.",
    chapeauFamille : "Change observed over the course of the programme, skill by skill: average level at the start " +
      "and at the end, what the skill measures, and its relevance in a professional context.",
    agrementCIR : "Rising Up is accredited as a research organisation under the French Research Tax Credit " +
      "(Crédit d'Impôt Recherche) by the Ministry of Higher Education and Research (2025 to 2027).",
    constatGlobal : "Over the course of the programme, the cohort changes, in knowledge and across several skills. " +
      "These results record a change observed during the programme; in the absence of a control group, " +
      "they do not on their own allow it to be causally attributed to the training. " +
      "The measurement distinguishes what is measured, what is observed and what may be interpreted.",
    debutParcours : "Start of programme",
    finParcours : "End of programme",
    vueEnsemble : "Overview by family",
    ceQueConstatent : "What these results show",
    methodologie : "Methodology and credibility framework",
    etudiantsMesures : "students measured",
    diplomes : "Degree(s)",
    periode : "Period",
    editeLe : (d) => `Issued on ${d}`,
    moyenneGroupe : "group average",
    modules : "modules",
    nombreEtudiants : "Number of students",
    autresCompetences : "Other skills measured",
    competences : (n) => (n > 1 ? "skills" : "skill"),
    ceQuElleMesure : "What it measures: ",
    pertinence : "Relevance: ",
    pertinencePrefixe : "useful in a professional context and for employability: ",
    seuilCourt : (v) => `threshold ${v}%`,
    plafond : " The starting level was already high: close to the ceiling, the gap falls within measurement noise.",
    constat : (nom, verbe, a, b) => `Over the course of the programme, ${nom} ${verbe} (${a}% to ${b}%).`,
    certifAtteint : (s) => `The cohort average is above the certification threshold (${s}%).`,
    certifNonAtteint : (s) => `The cohort average remains below the certification threshold (${s}%).`,
    synthesePhrase : (n, gain, hausse, stable, baisse) =>
      `Over the course of the programme, across ${n} student${n > 1 ? "s" : ""}, ` +
      `the cohort's knowledge level moves by ${gain >= 0 ? "+" + gain : gain} points. ` +
      `Skills improve on ${hausse}, remain stable on ${stable} ` +
      `and readjust on ${baisse}.`,
  },
}

const t = (langue) => T[langueDe(langue)]

/* R.3 — verbes FACTUELS imposes par Nawal. Un ecart d'au moins 2 points compte
   comme un mouvement, en dessous c'est du bruit de mesure. */
const SEUIL_MOUVEMENT = 2
const VERBES = {
  fr : { hausse: "a progressé", baisse: "s'est réajustée", stable: "est restée stable" },
  en : { hausse: "improved", baisse: "readjusted", stable: "remained stable" },
}
const mouvement = (avantBrut, apresBrut, langue) => {
  const v = VERBES[langueDe(langue)]
  const delta = Math.round(nombre(apresBrut)) - Math.round(nombre(avantBrut))
  if (delta >= SEUIL_MOUVEMENT) return { sens: "hausse", verbe: v.hausse, fleche: "↗", couleur: VERT, libelle: v.hausse }
  if (delta <= -SEUIL_MOUVEMENT) return { sens: "baisse", verbe: v.baisse, fleche: "↘", couleur: ORANGE, libelle: v.baisse }
  return { sens: "stable", verbe: v.stable, fleche: "→", couleur: ENCRE, libelle: v.stable }
}

/* Page finale — les CINQ paragraphes de methodologie de la fiche V5 de Nawal
   (point 6), mot pour mot. Ils remplacent le paragraphe unique de la V3, juge
   trop court pour porter la credibilite du rapport. Ne pas les reecrire sans
   elle : chaque phrase est un engagement scientifique ou juridique (referentiel
   depose sur OSF, agrement CIR, absence de groupe controle).
   ⚠️ La version anglaise est une traduction fidele a faire valider par Nawal :
   ces cinq paragraphes engagent Rising Up sur le fond scientifique et juridique.
   En cas de doute, le francais fait foi. */
const METHODOLOGIE = [
  {
    titre: "Une mesure des compétences, pas de la personnalité.",
    titreEn: "Measuring skills, not personality.",
    texte: "Rising Up mesure des compétences humaines qui peuvent être mobilisées, développées et renforcées au cours d'un parcours. Notre approche s'intéresse ainsi à des capacités utiles pour apprendre, travailler et s'adapter à différents contextes professionnels, et non à des traits de personnalité considérés comme relativement stables.",
    texteEn: "Rising Up measures human skills that can be mobilised, developed and strengthened over the course of a programme. Our approach therefore focuses on capabilities that are useful for learning, working and adapting to different professional contexts, rather than on personality traits regarded as relatively stable.",
  },
  {
    titre: "Un référentiel fondé sur la recherche et confronté au monde professionnel.",
    titreEn: "A framework grounded in research and tested against the professional world.",
    texte: "Les évaluations s'appuient sur le référentiel scientifique Rising Up de 18 compétences. Il a été construit à partir de la littérature scientifique en sciences cognitives, puis confronté empiriquement aux compétences recherchées dans les diplômes, les certifications professionnelles et les offres d'emploi. Cette double approche permet d'articuler les connaissances issues de la recherche avec les compétences effectivement attendues dans les parcours de formation et sur le marché du travail. Le référentiel est public et déposé sur OSF ; les instruments d'évaluation développés par Rising Up sont propriétaires.",
    texteEn: "The assessments draw on the Rising Up scientific framework of 18 skills. It was built from the scientific literature in cognitive science, then tested empirically against the skills sought in qualifications, professional certifications and job postings. This twofold approach makes it possible to connect knowledge from research with the skills actually expected in training pathways and on the labour market. The framework is public and deposited on OSF; the assessment instruments developed by Rising Up are proprietary.",
  },
  {
    titre: "Une lecture rigoureuse de l'évolution.",
    titreEn: "A rigorous reading of change.",
    texte: "Lorsque plusieurs mesures sont réalisées au cours d'un parcours, elles permettent d'observer comment les compétences et les connaissances évoluent dans le temps. Rising Up distingue systématiquement ce qui est mesuré, ce qui est observé et ce qui peut être interprété. En l'absence de groupe contrôle, une évolution observée n'est donc pas présentée comme la preuve d'un effet causal de la formation.",
    texteEn: "When several measurements are taken over the course of a programme, they make it possible to observe how skills and knowledge evolve over time. Rising Up systematically distinguishes what is measured, what is observed and what may be interpreted. In the absence of a control group, an observed change is therefore not presented as proof of a causal effect of the training.",
  },
  {
    titre: "Une expertise scientifique au cœur du dispositif.",
    titreEn: "Scientific expertise at the heart of the approach.",
    texte: "La méthodologie est développée par une équipe pluridisciplinaire associant expertise en sciences cognitives, psychométrie, sciences des données et ingénierie pédagogique. Cette articulation entre recherche scientifique, mesure et usages de terrain guide aussi bien la conception des outils que l'interprétation des résultats.",
    texteEn: "The methodology is developed by a multidisciplinary team combining expertise in cognitive science, psychometrics, data science and instructional design. This articulation between scientific research, measurement and field use guides both the design of the tools and the interpretation of the results.",
  },
  {
    titre: "Des garanties de qualité et de transparence.",
    titreEn: "Guarantees of quality and transparency.",
    texte: "Rising Up est agréé au titre du Crédit d'Impôt Recherche par le ministère chargé de la Recherche pour la période 2025 à 2027 et engagé dans une démarche de Numérique Responsable. Les résultats sont analysés à l'échelle des compétences et des cohortes, afin de restituer les évolutions observées avec leur contexte plutôt que de les réduire à un indicateur unique.",
    texteEn: "Rising Up is accredited under the French Research Tax Credit (Crédit d'Impôt Recherche) by the ministry responsible for Research for the period 2025 to 2027, and is committed to a Responsible Digital approach. Results are analysed at the level of skills and cohorts, in order to report observed changes together with their context rather than reducing them to a single indicator.",
  },
]

/* Point 5 — phrase du Label Numerique Responsable, texte de Nawal mot pour mot.
   Elle remplace la mention « Signataire du Label Numerique Responsable » posee
   faute de mieux le 03/09 : le niveau detenu y est desormais dit. */
const PHRASE_LABEL_NR = {
  fr : "Le Label Numérique Responsable atteste d'une démarche de réduction des impacts environnementaux et sociaux du numérique. Créé par l'Institut du Numérique Responsable (avec l'ADEME et le WWF) et opéré par l'agence Lucie, Rising Up en détient le niveau 1.",
  en : "The Responsible Digital label (Label Numérique Responsable) attests to an approach aimed at reducing the environmental and social impacts of digital technology. Created by the Institut du Numérique Responsable (with ADEME and WWF) and operated by the Lucie agency, Rising Up holds level 1.",
}

/* ---- en-tete des pages interieures ---- */
const TopForPage = ({ page, pageTot, langue }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "30px" }}>
    <img src={logoNoir} style={{ width: "187px", height: "35px" }} alt="" />
    <Box sx={{ textAlign: "right" }}>
      <Box sx={{ fontSize: "30px" }}>{t(langue).titreCourt}</Box>
      <Box sx={{ fontSize: "16px", marginTop: "4px", color: "rgba(10,10,11,0.6)" }}>{t(langue).pageXsurY(page, pageTot)}</Box>
    </Box>
  </Box>
)

const Page = ({ children, fond }) => (
  <Box sx={{
    width: pageDimensions.width, minWidth: pageDimensions.width, maxWidth: pageDimensions.width,
    height: pageDimensions.height, minHeight: pageDimensions.height, maxHeight: pageDimensions.height,
    background: fond || "white",
    color: fond ? "white" : ENCRE,
    padding: "70px 80px",
    boxSizing: "border-box",
    overflow: "hidden",
    position: "relative",
    fontFamily: "Questrial",
  }}>
    {children}
  </Box>
)

const CarteChiffre = ({ valeur, libelle }) => (
  <Box sx={{ border: "2px solid " + ENCRE, padding: "26px 20px", flex: 1, textAlign: "center" }}>
    <Box sx={{ fontSize: "46px" }}>{valeur}</Box>
    <Box sx={{ fontSize: "17px", marginTop: "8px", color: "rgba(10,10,11,0.65)" }}>{libelle}</Box>
  </Box>
)

const BarreConnaissance = ({ libelle, valeur: valeurBrute, couleur }) => {
  const valeur = nombre(valeurBrute)
  return (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
    <Box sx={{ width: "150px", height: "360px", border: "2px solid " + ENCRE, position: "relative" }}>
      <Box sx={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: Math.max(3, Math.min(100, valeur)) + "%",
        background: couleur,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        color: "white", fontSize: "28px", paddingTop: "10px",
      }}>
        {Math.round(valeur)} %
      </Box>
    </Box>
    <Box sx={{ fontSize: "19px" }}>{libelle}</Box>
  </Box>
  )
}

/* Une competence : jauge avant -> apres, puis le texte en TROIS TEMPS voulu par
   Nawal (R.3) — constat, ce qu'elle mesure, pertinence. */
const BlocCompetence = ({ entree, cle, avant: avantBrut, apres: apresBrut, seuil, langue, nbrDebut, nbrFin }) => {
  const avant = nombre(avantBrut), apres = nombre(apresBrut)
  const a = Math.round(avant), b = Math.round(apres)
  const m = mouvement(avant, apres, langue)
  const nom = entree ? libelleComplet(entree.cle, langue) : cle

  /* ⚠️ 04/09 — UNE MESURE ABSENTE N'EST PAS UNE MESURE A ZERO.
     Quand aucun apprenant n'a passe le module qui evalue cette competence, la
     moyenne arrivait ici a 0 et le rapport imprimait « 72 % a 0 % », puis
     « s'est reajustee » : une chute inventee, sur un document qui part chez le
     client. C'est la regle que Nawal applique partout — ne jamais afficher
     plus que ce qui a ete mesure. Les compteurs viennent de Progression.js ;
     s'ils sont absents (ancien appelant), on suppose la mesure faite pour ne
     rien casser. */
  const debutMesure = nbrDebut === undefined || nbrDebut > 0
  const finMesuree = nbrFin === undefined || nbrFin > 0
  const complet = debutMesure && finMesuree

  /* Constat. Une baisse sur une competence DEJA HAUTE se lit differemment : on
     est proche du plafond et l'ecart tient dans le bruit de mesure. */
  let constat
  if (complet) {
    constat = t(langue).constat(nom, m.verbe, a, b)
    if (m.sens === "baisse" && a >= 75) {
      constat += t(langue).plafond
    }
  } else if (debutMesure) {
    constat = t(langue).finNonMesuree(nom, a)
  } else if (finMesuree) {
    constat = t(langue).debutNonMesure(nom, b)
  } else {
    constat = t(langue).nonMesuree(nom)
  }
  /* Certification : uniquement si un seuil est configure pour cette competence.
     Rien plutot qu'un chiffre invente (reponse Q2 d'Hicham du 03/09). */
  /* ⚠️ 04/09 — CE QUE CETTE PHRASE PEUT DIRE, ET CE QU'ELLE NE PEUT PAS.
     `b` est la moyenne de la PROMOTION sur cette competence. Le seuil, lui, est
     une regle INDIVIDUELLE : l'ecran Certification compare le score de chaque
     apprenant a ce meme seuil et le classe un par un en acquis / rattrapage.
     Une moyenne au-dessus du seuil ne dit donc rien de la certification de qui
     que ce soit — une promotion a 52 de moyenne pour un seuil a 50 peut tres
     bien compter une moitie d'apprenants sous la barre.
     Le rapport ecrivait « Le niveau atteint a permis sa certification », une
     conclusion individuelle tiree d'un chiffre collectif, dans un document qui
     part chez le client. Il enonce desormais le fait mesure, et rien de plus.
     ⚠️ A faire relire par Nawal : ce sont ses textes, j'ai change celui-ci
     parce qu'il affirmait quelque chose de faux, pas par preference. */
  let phraseCertif = null
  if (finMesuree && typeof seuil === "number" && Number.isFinite(seuil)) {
    phraseCertif = b >= seuil
      ? t(langue).certifAtteint(seuil)
      : t(langue).certifNonAtteint(seuil)
  }

  return (
    <Box sx={{ marginBottom: "30px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "10px", gap: "20px" }}>
        <Box sx={{ fontSize: "22px", lineHeight: "125%" }}>{nom}</Box>
        <Box sx={{ fontSize: "20px", color: complet ? m.couleur : "rgba(10,10,11,0.45)", whiteSpace: "nowrap" }}>
          {complet && <>{a} % {m.fleche} {b} %</>}
          {!complet && debutMesure && <>{a} % {"\u2192"} {t(langue).pasEncoreMesure}</>}
          {!complet && !debutMesure && finMesuree && <>{t(langue).pasEncoreMesure} {"\u2192"} {b} %</>}
          {!debutMesure && !finMesuree && <>{t(langue).pasEncoreMesure}</>}
        </Box>
      </Box>
      <Box sx={{ position: "relative", height: "16px", background: "#EBE7E4" }}>
        {debutMesure &&
          <>
            <Box sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: Math.min(100, avant) + "%", background: "rgba(10,10,11,0.35)" }} />
            <Box sx={{ position: "absolute", top: "-5px", left: `calc(${Math.min(100, avant)}% - 3px)`, width: "4px", height: "26px", background: ENCRE }} />
          </>
        }
        {finMesuree &&
          <Box sx={{ position: "absolute", top: "-5px", left: `calc(${Math.min(100, apres)}% - 3px)`, width: "4px", height: "26px", background: m.couleur === ENCRE ? GRIS : m.couleur }} />
        }
        {/* Relecture du PDF, 03/09 : ce repere etait un filet gris de 2 px que
            personne ne remarquait, et rien ne disait ce qu'il marquait. */}
        {typeof seuil === "number" && Number.isFinite(seuil) &&
          <>
            <Box sx={{ position: "absolute", top: "-11px", bottom: "-11px", left: `calc(${Math.min(100, seuil)}% - 1px)`, width: "3px", background: ENCRE }} />
            {/* Le libelle passe SOUS la barre : au-dessus, il heurtait les
                titres longs comme « Rigueur et respect des regles (incluant
                autonomie operationnelle) ». */}
            <Box sx={{ position: "absolute", top: "18px", left: `calc(${Math.min(100, seuil)}% + 7px)`, fontSize: "14px", color: "rgba(10,10,11,0.7)", whiteSpace: "nowrap" }}>
              {t(langue).seuilCourt(Math.round(seuil))}
            </Box>
          </>
        }
      </Box>
      <Box sx={{ fontSize: "16px", lineHeight: "150%", marginTop: "30px", color: "rgba(10,10,11,0.85)" }}>
        {constat}{phraseCertif ? " " + phraseCertif : ""}
      </Box>
      {entree &&
        <>
          <Box sx={{ fontSize: "16px", lineHeight: "150%", marginTop: "6px", color: "rgba(10,10,11,0.7)" }}>
            <Box component="span" sx={{ color: ENCRE }}>{t(langue).ceQuElleMesure}</Box>{mesureDe(entree, langue)}.
          </Box>
          <Box sx={{ fontSize: "16px", lineHeight: "150%", marginTop: "4px", color: "rgba(10,10,11,0.7)" }}>
            <Box component="span" sx={{ color: ENCRE }}>{t(langue).pertinence}</Box>
            {t(langue).pertinencePrefixe}{pertinenceDe(entree, langue)}.
          </Box>
        </>
      }
    </Box>
  )
}

const RapportImpact = React.forwardRef(({ ecoleName, groupeName, diplomes, periode, stats, connaissance, competences, seuils, langue }, ref) => {

  const liste = Array.isArray(competences) ? competences : []
  const connaissanceSure = { avant: nombre(connaissance?.avant), apres: nombre(connaissance?.apres) }
  const gain = Math.round(connaissanceSure.apres) - Math.round(connaissanceSure.avant)

  const enHausse = liste.filter((c) => mouvement(c.avant, c.apres, langue).sens === "hausse")
  const enBaisse = liste.filter((c) => mouvement(c.avant, c.apres, langue).sens === "baisse")
  const stables = liste.filter((c) => mouvement(c.avant, c.apres, langue).sens === "stable")

  const seuilDe = (cle) => {
    if (!seuils) return undefined
    const v = seuils[cle]
    return typeof v === "number" && Number.isFinite(v) ? v : undefined
  }

  /* R.1 — les familles viennent du referentiel, dans son ordre, et chaque
     competence recue est rattachee par sa cle. Ce qui n'est pas au referentiel
     n'est pas perdu : il part dans un dernier groupe, pour qu'un contenu qui
     evolue ne fasse pas disparaitre silencieusement une competence du rapport. */
  const apparie = liste.map((c) => ({ ...c, entree: trouverCompetence(c.key) }))
  const groupes = Object.keys(FAMILLES)
    .sort((x, y) => FAMILLES[x].ordre - FAMILLES[y].ordre)
    .map((f) => ({
      cle: f,
      libelle: (langueDe(langue) === "en" ? (FAMILLES[f].libelleEn || FAMILLES[f].libelle) : FAMILLES[f].libelle),
      skills: apparie.filter((c) => c.entree && c.entree.famille === f),
    }))
    .filter((g) => g.skills.length > 0)
  const orphelines = apparie.filter((c) => !c.entree)
  if (orphelines.length > 0) {
    groupes.push({ cle: "_autres", libelle: t(langue).autresCompetences, skills: orphelines })
  }

  const pageTot = 3 + groupes.length + 1

  /* R.3 — phrase de synthese, texte de Nawal */
  /* B51 (25/09) : sans aucune mesure, le rapport affichait « 0 % → 0 %,
     +0 pts, 0 étudiant » comme un resultat. On le dit. */
  // « Aucune mesure » exige TOUT : zero etudiant mesure (debut ET fin), aucun
  // niveau de connaissance, pas de moyenne, et aucune competence mesuree meme
  // en debut de parcours. Un groupe en milieu de parcours (mesure de debut,
  // pas encore de fin) n'est donc PAS declare « jamais mesure » (relecture du
  // 25/09 : il l'etait, a cote d'une moyenne de 62 /100).
  /* L'encart « niveau de connaissance » ne parle que des tests de connaissance :
     sans eux, « pas encore mesuré », meme si le groupe a des notes de quiz
     (relecture du 25/09 : « +0 pts, 0 % → 0 % » revenait dans ce cas). */
  const connaissanceMesuree = !!(connaissanceSure.avant || connaissanceSure.apres)
  const aucuneMesure = !((stats?.etudiantsMesures || 0) > 0)
    && !connaissanceSure.avant && !connaissanceSure.apres
    && (stats?.moyenne === undefined || stats?.moyenne === null || stats?.moyenne === '-')
    && !(competences || []).some((c) => (c?.nbrDebut || 0) > 0 || (c?.nbrFin || 0) > 0)
  const phraseSynthese = aucuneMesure ? t(langue).syntheseAucuneMesure : t(langue).synthesePhrase(
    stats?.etudiantsMesures || 0, gain, enHausse.length, stables.length, enBaisse.length)

  return (
    <Box ref={ref} sx={{ position: "absolute", top: "-100000px", left: 0, zIndex: -50, fontFamily: "Questrial" }}>

      {/* ---- Page 1 : couverture ---- */}
      <Page fond={`linear-gradient(160deg, ${ENCRE} 55%, #2A2A2E 100%)`}>
        <img src={logoBlanc} style={{ width: "220px" }} alt="" />
        <Box sx={{ marginTop: "420px" }}>
          <Box sx={{ width: "520px", height: "3px", background: ORANGE, marginBottom: "40px" }} />
          <Box sx={{ fontSize: "88px", lineHeight: "110%" }}>{t(langue).titreCourt}</Box>
          <Box sx={{ fontSize: "30px", marginTop: "30px", color: "rgba(255,255,255,0.85)" }}>
            {ecoleName}{groupeName ? " · " + groupeName : ""}
          </Box>
        </Box>
        <Box sx={{ position: "absolute", bottom: "90px", left: "80px", right: "80px", fontSize: "21px", lineHeight: "185%", color: "rgba(255,255,255,0.85)" }}>
          {diplomes ? <Box>{t(langue).diplomes}{langueDe(langue) === "en" ? ": " : "\u00a0: "}{diplomes}</Box> : null}
          <Box>{t(langue).periode}{langueDe(langue) === "en" ? ": " : "\u00a0: "}{periode}</Box>
          <Box>{t(langue).nombreEtudiants}{langueDe(langue) === "en" ? ": " : "\u00a0: "}{stats?.etudiants || 0}</Box>
          <Box>{t(langue).editeLe(moment().locale(langueDe(langue)).format("D MMMM YYYY"))}</Box>
        </Box>
      </Page>

      {/* ---- Page 2 : synthese ---- */}
      <Page>
        <TopForPage page={2} pageTot={pageTot} langue={langue} />
        <Box sx={{ fontSize: "44px", marginBottom: "12px" }}>{t(langue).synthese}</Box>
        <Box sx={{ width: "160px", height: "3px", background: ORANGE, marginBottom: "36px" }} />
        <Box sx={{ fontSize: "23px", lineHeight: "155%", marginBottom: "16px" }}>{phraseSynthese}</Box>
        <Box sx={{ fontSize: "18px", lineHeight: "150%", marginBottom: "44px", color: "rgba(10,10,11,0.6)" }}>
          {t(langue).perimetre}
        </Box>

        {/* Relecture du PDF genere, 03/09 : une carte annoncait « 100 % de taux de
            complétion » sur toutes les promotions. Le calcul rapportait les
            apprenants du graphe a eux-memes — il valait donc 100 % par
            construction, et contredisait le 80 % affiche dans le Suivi. Un
            chiffre faux dans un document client est pire que pas de chiffre :
            la carte est retiree, et la portee du rapport est dite en toutes
            lettres juste au-dessus. */}
        <Box sx={{ display: "flex", gap: "18px", marginBottom: "70px" }}>
          {/* « mesures » : ceux qui ont une mesure de debut ET de fin, la seule
              population sur laquelle l'evolution est calculee. Pas l'effectif
              de la promotion, qui reste en couverture. */}
          <CarteChiffre valeur={stats?.etudiantsMesures || 0} libelle={t(langue).etudiantsMesures} />
          <CarteChiffre valeur={(stats?.moyenne !== undefined ? stats.moyenne : "-") + " /100"} libelle={t(langue).moyenneGroupe} />
          <CarteChiffre valeur={stats?.nbrModules || 0} libelle={t(langue).modules} />
          <CarteChiffre valeur={stats?.nbrSoftSkills || 0} libelle={t(langue).softSkills} />
        </Box>

        <Box sx={{ fontSize: "32px", marginBottom: "30px" }}>{t(langue).niveauConnaissance}</Box>
        <Box sx={{ display: "flex", gap: "90px", alignItems: "flex-end", justifyContent: "center", marginBottom: "36px" }}>
          <BarreConnaissance libelle={t(langue).debutParcours} valeur={connaissanceSure.avant} couleur="rgba(10,10,11,0.45)" />
          <BarreConnaissance libelle={t(langue).finParcours} valeur={connaissanceSure.apres} couleur={ORANGE} />
          <Box sx={{ paddingBottom: "180px", fontSize: "56px", color: ORANGE }}>{!connaissanceMesuree ? t(langue).pasEncoreMesure : <>{gain >= 0 ? "+" : ""}{gain} pts</>}</Box>
        </Box>
        <Box sx={{ fontSize: "20px", lineHeight: "150%", textAlign: "center", color: "rgba(10,10,11,0.8)" }}>
          {!connaissanceMesuree ? t(langue).pasEncoreMesure : t(langue).evolutionConnaissance(Math.round(connaissanceSure.avant), Math.round(connaissanceSure.apres), gain)}
        </Box>
      </Page>

      {/* ---- Page 3 : vue d'ensemble par famille + verdict ---- */}
      <Page>
        <TopForPage page={3} pageTot={pageTot} langue={langue} />
        <Box sx={{ fontSize: "44px", marginBottom: "12px" }}>{t(langue).vueEnsemble}</Box>
        <Box sx={{ width: "160px", height: "3px", background: ORANGE, marginBottom: "46px" }} />

        {groupes.map((g) => {
          const moyAvant = g.skills.length ? g.skills.reduce((acc, c) => acc + nombre(c.avant), 0) / g.skills.length : 0
          const moyApres = g.skills.length ? g.skills.reduce((acc, c) => acc + nombre(c.apres), 0) / g.skills.length : 0
          return (
            <Box key={g.cle} sx={{ marginBottom: "44px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "12px" }}>
                <Box sx={{ fontSize: "26px" }}>{g.libelle}</Box>
                <Box sx={{ fontSize: "20px", color: "rgba(10,10,11,0.65)" }}>{g.skills.length} {t(langue).competences(g.skills.length)}</Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <Box sx={{ width: "150px", fontSize: "17px", color: "rgba(10,10,11,0.6)" }}>{t(langue).debutParcours}</Box>
                  <Box sx={{ height: "24px", width: (moyAvant * 8) + "px", background: "rgba(10,10,11,0.35)" }} />
                  <Box sx={{ fontSize: "19px" }}>{Math.round(moyAvant)} %</Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <Box sx={{ width: "150px", fontSize: "17px", color: "rgba(10,10,11,0.6)" }}>{t(langue).finParcours}</Box>
                  <Box sx={{ height: "24px", width: (moyApres * 8) + "px", background: ORANGE }} />
                  <Box sx={{ fontSize: "19px" }}>{Math.round(moyApres)} %</Box>
                </Box>
              </Box>
            </Box>
          )
        })}

        {/* R.3 — verdict NON CAUSAL, texte de Nawal mot pour mot */}
        <Box sx={{ marginTop: "50px", border: "2px solid " + ENCRE, padding: "34px 38px" }}>
          <Box sx={{ fontSize: "26px", marginBottom: "16px" }}>{t(langue).ceQueConstatent}</Box>
          <Box sx={{ fontSize: "19px", lineHeight: "160%", color: "rgba(10,10,11,0.85)" }}>
            {aucuneMesure ? t(langue).syntheseAucuneMesure : t(langue).constatGlobal}
          </Box>
        </Box>
      </Page>

      {/* ---- Une page par famille ---- */}
      {groupes.map((g, iFam) => (
        <Page key={g.cle}>
          <TopForPage page={4 + iFam} pageTot={pageTot} langue={langue} />
          <Box sx={{ fontSize: "44px", marginBottom: "12px" }}>{g.libelle}</Box>
          <Box sx={{ width: "160px", height: "3px", background: ORANGE, marginBottom: "20px" }} />
          <Box sx={{ fontSize: "18px", lineHeight: "150%", color: "rgba(10,10,11,0.7)", marginBottom: "34px" }}>
            {t(langue).chapeauFamille}
          </Box>
          {g.skills.map((c) => (
            <BlocCompetence
              key={c.key}
              cle={c.key}
              entree={c.entree}
              avant={c.avant}
              apres={c.apres}
              seuil={seuilDe(c.key)}
              nbrDebut={c.nbrDebut}
              nbrFin={c.nbrFin}
              langue={langue}
            />
          ))}
        </Page>
      ))}

      {/* ---- Page finale : methodologie + bandeau credibilite ---- */}
      <Page>
        <TopForPage page={pageTot} pageTot={pageTot} langue={langue} />
        <Box sx={{ fontSize: "44px", marginBottom: "12px" }}>{t(langue).methodologie}</Box>
        <Box sx={{ width: "160px", height: "3px", background: ORANGE, marginBottom: "34px" }} />
        {/* Point 6 — les cinq paragraphes de Nawal. Le chapeau de chaque
            paragraphe est distingue par la COULEUR, jamais par la graisse :
            Questrial n'existe qu'en 400 sur ce poste, un font-weight superieur
            serait synthetise par le navigateur et donnerait, dans le PDF, la
            fausse impression d'une seconde police. */}
        <Box sx={{ maxWidth: "1020px" }}>
          {METHODOLOGIE.map((p) => (
            <Box key={p.titre} sx={{ marginBottom: "30px" }}>
              <Box sx={{ fontSize: "21px", lineHeight: "150%", marginBottom: "4px", color: ENCRE }}>{langueDe(langue) === "en" ? (p.titreEn || p.titre) : p.titre}</Box>
              <Box sx={{ fontSize: "18px", lineHeight: "165%", color: "rgba(10,10,11,0.8)" }}>{langueDe(langue) === "en" ? (p.texteEn || p.texte) : p.texte}</Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ position: "absolute", bottom: "70px", left: "80px", right: "80px" }}>
          <Box sx={{ height: "1px", background: "rgba(10,10,11,0.25)", marginBottom: "22px" }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "30px" }}>
            <Box sx={{ fontSize: "15px", lineHeight: "165%", color: "rgba(10,10,11,0.6)", maxWidth: "880px" }}>
              {t(langue).agrementCIR}
              <br />
              {/* Nawal demande aussi le logo du Label. Le fichier qu'elle indique
                  (prospection commerciale/propale_medef/assets/label-nr.png) n'existe
                  toujours pas sur ce poste : la mention reste en texte. */}
              {PHRASE_LABEL_NR[langueDe(langue)]}
            </Box>
            <img src={logoNoir} style={{ width: "150px" }} alt="" />
          </Box>
        </Box>
      </Page>

    </Box>
  )
})

// Relecture 02/09 (3e passe) : memo, pour que des props identiques ne
// reconstruisent pas les pages du rapport.
export default React.memo(RapportImpact)
